import React, { useCallback, useEffect, useMemo, useState } from "react";
import { api, ApiClientError } from "../api";
import type { BoardCard, BoardColumn, Id, Pipeline, SavedView, StaffUser } from "../types";
import KanbanColumn from "../components/KanbanColumn";
import TransitionModal from "../components/TransitionModal";
import { useToast } from "../context/ToastContext";
import { useDrawer } from "../context/DrawerContext";
import { validTargets } from "../lib/transitions";

interface Filters {
  owner: string;
  source: string;
  service: string;
  temperature: string;
  search: string;
}

const EMPTY_FILTERS: Filters = { owner: "", source: "", service: "", temperature: "", search: "" };

interface DraggingCard {
  card: BoardCard;
  stageCode: string;
}

interface PendingTransition {
  card: BoardCard;
  toStageCode: string;
  need: "owner" | "reason";
}

export default function Kanban() {
  const { show } = useToast();
  const { open } = useDrawer();

  const [pipelines, setPipelines] = useState<Pipeline[]>([]);
  const [pipeline, setPipeline] = useState(
    () => new URLSearchParams(window.location.search).get("pipeline") || "acquisition"
  );
  const [columns, setColumns] = useState<BoardColumn[]>([]);
  const [loading, setLoading] = useState(false);
  const [users, setUsers] = useState<StaffUser[]>([]);
  const [views, setViews] = useState<SavedView[]>([]);
  const [filters, setFilters] = useState<Filters>(EMPTY_FILTERS);
  const [dragging, setDragging] = useState<DraggingCard | null>(null);
  const [pending, setPending] = useState<PendingTransition | null>(null);

  useEffect(() => {
    api.pipelines().then(setPipelines).catch(() => {});
    api.users().then(setUsers).catch(() => {});
    api.getViews().then(setViews).catch(() => {});
  }, []);

  const loadBoard = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.getBoard({
        pipeline,
        owner: filters.owner || undefined,
        source: filters.source || undefined,
        service: filters.service || undefined,
        temperature: filters.temperature || undefined,
        search: filters.search || undefined,
      });
      setColumns(res.columns);
    } catch (e) {
      if (e instanceof ApiClientError) show(e.message, "error");
      else show("No se pudo cargar el tablero.", "error");
    } finally {
      setLoading(false);
    }
  }, [pipeline, filters, show]);

  useEffect(() => {
    loadBoard();
  }, [loadBoard]);

  const { sourceOptions, serviceOptions } = useMemo(() => {
    const sources = new Set<string>();
    const services = new Set<string>();
    columns.forEach((c) => c.cards.forEach((card) => {
      if (card.source_channel) sources.add(card.source_channel);
      if (card.service_name) services.add(card.service_name);
    }));
    return { sourceOptions: Array.from(sources).sort(), serviceOptions: Array.from(services).sort() };
  }, [columns]);

  const validTargetStages = useMemo(() => {
    if (!dragging) return new Set<string>();
    return new Set(validTargets(dragging.stageCode));
  }, [dragging]);

  async function attemptMove(cardId: Id, toStageCode: string, extra?: { owner_user_id?: Id; reason?: string }) {
    try {
      await api.moveStage(cardId, { to_stage_code: toStageCode, ...extra });
      show("Etapa actualizada.", "success");
      loadBoard();
    } catch (e) {
      if (e instanceof ApiClientError) {
        if (e.code === "owner_required") {
          const card = columns.flatMap((c) => c.cards).find((c) => c.id === cardId);
          if (card) setPending({ card, toStageCode, need: "owner" });
          return;
        }
        if (e.code === "reason_required") {
          const card = columns.flatMap((c) => c.cards).find((c) => c.id === cardId);
          if (card) setPending({ card, toStageCode, need: "reason" });
          return;
        }
        show(e.message, "error");
      } else {
        show("No se pudo mover la tarjeta.", "error");
      }
    }
  }

  function handleDrop(stageCode: string) {
    if (!dragging) return;
    if (dragging.stageCode === stageCode) {
      setDragging(null);
      return;
    }
    const cardId = dragging.card.id;
    setDragging(null);
    attemptMove(cardId, stageCode);
  }

  async function handleOpenCard(card: BoardCard) {
    try {
      const opp = await api.getOpportunity(card.id);
      if (opp.contact_id) open(opp.contact_id);
      else show("No se encontró el contacto asociado.", "error");
    } catch (e) {
      show("No se pudo abrir la ficha 360°.", "error");
    }
  }

  async function saveView() {
    const name = window.prompt("Nombre de la vista guardada:");
    if (!name) return;
    try {
      const view = await api.createView({
        name,
        filters: { pipeline, ...filters },
      });
      setViews((v) => [...v, view]);
      show("Vista guardada.", "success");
    } catch (e) {
      show("No se pudo guardar la vista.", "error");
    }
  }

  function applyView(view: SavedView) {
    const f = view.filters || {};
    setPipeline(f.pipeline || "acquisition");
    setFilters({
      owner: f.owner || "",
      source: f.source || "",
      service: f.service || "",
      temperature: f.temperature || "",
      search: f.search || "",
    });
  }

  const totalCards = columns.reduce((sum, c) => sum + c.cards.length, 0);
  const hotCards = columns.reduce((sum, c) => sum + c.cards.filter((x) => x.temperature === "hot").length, 0);
  const overdueCards = columns.reduce((sum, c) => sum + c.cards.filter((x) => x.sla?.status === "breach").length, 0);

  return (
    <div className="kanban-page">
      <div className="kanban-header">
        <div>
          <h1>Pipeline comercial</h1>
          <div className="kpi">
            <div>
              Oportunidades <b>{totalCards}</b>
            </div>
            <div>
              Alta intención <b>{hotCards}</b>
            </div>
            <div>
              SLA vencido <b className="overdue">{overdueCards}</b>
            </div>
          </div>
        </div>
      </div>

      <div className="pipeline-tabs">
        {pipelines.length > 0
          ? pipelines.map((p) => (
              <button key={p.code} className={`tab ${p.code === pipeline ? "active" : ""}`} onClick={() => setPipeline(p.code)}>
                {p.name}
              </button>
            ))
          : ["acquisition", "sales", "appointment", "post_sale"].map((code) => (
              <button key={code} className={`tab ${code === pipeline ? "active" : ""}`} onClick={() => setPipeline(code)}>
                {code}
              </button>
            ))}
      </div>

      <div className="filters">
        <select value={filters.owner} onChange={(e: any) => setFilters((f) => ({ ...f, owner: e.target.value }))}>
          <option value="">Todas las vendedoras</option>
          {users.map((u) => (
            <option key={u.id} value={u.id}>
              {u.full_name}
            </option>
          ))}
        </select>
        <select value={filters.source} onChange={(e: any) => setFilters((f) => ({ ...f, source: e.target.value }))}>
          <option value="">Todas las fuentes</option>
          {sourceOptions.map((s) => (
            <option key={s} value={s}>
              {s}
            </option>
          ))}
        </select>
        <select value={filters.service} onChange={(e: any) => setFilters((f) => ({ ...f, service: e.target.value }))}>
          <option value="">Todos los servicios</option>
          {serviceOptions.map((s) => (
            <option key={s} value={s}>
              {s}
            </option>
          ))}
        </select>
        <select value={filters.temperature} onChange={(e: any) => setFilters((f) => ({ ...f, temperature: e.target.value }))}>
          <option value="">Toda temperatura</option>
          <option value="hot">🔥 Alta</option>
        </select>
        <input
          className="chip-search"
          placeholder="Buscar en el tablero..."
          value={filters.search}
          onChange={(e: any) => setFilters((f) => ({ ...f, search: e.target.value }))}
        />
        {(filters.owner || filters.source || filters.service || filters.temperature || filters.search) && (
          <button className="chip" onClick={() => setFilters(EMPTY_FILTERS)}>
            Limpiar filtros
          </button>
        )}
        <button className="chip" onClick={saveView}>
          Guardar vista
        </button>
        {views.map((v) => (
          <button key={v.id} className="chip" onClick={() => applyView(v)}>
            {v.name}
          </button>
        ))}
      </div>

      {loading && <div className="loading-strip">Cargando tablero...</div>}

      <section className="board">
        {columns.map((col) => (
          <KanbanColumn
            key={col.stage_code}
            column={col}
            draggingCard={dragging?.card ?? null}
            isValidTarget={validTargetStages.has(col.stage_code)}
            onDragStart={(card) => setDragging({ card, stageCode: col.stage_code })}
            onDragEnd={() => setDragging(null)}
            onDrop={handleDrop}
            onOpen={handleOpenCard}
          />
        ))}
        {!loading && columns.length === 0 && <div className="col-empty">Sin datos para este pipeline.</div>}
      </section>

      {pending && (
        <TransitionModal
          need={pending.need}
          users={users}
          onCancel={() => setPending(null)}
          onConfirm={(value) => {
            const p = pending;
            setPending(null);
            attemptMove(p.card.id, p.toStageCode, value);
          }}
        />
      )}
    </div>
  );
}
